import React, { forwardRef, useMemo } from "react";
import { useModal } from "../../../../hooks/useModal";
import "../styles/song.scss";
import { ReactComponent as PlayIcon } from "../../../../images/play.svg";
import { ReactComponent as PlayIconFlat } from "../../../../images/play-flat.svg";
import Button from "../../../basic/Button";
import { useDisplayRadio, useNextSong } from "../../../../hooks/useApi";
import { ADD_SONG_TO_RADIO_MODAL, VOTE_MODAL } from "../../../../constants";
import FavoriteButton from "../../../FavoriteButton";
import LoadableImage from "../../../basic/LoadableImage";
import colorFromString from "../../../../utils/colorFromString";
import cleanSongTitle from "../../../../utils/cleanSongTitle";
// import DevPre from "../../../dev/DevPre";

const Song = forwardRef(
  ({ songid, songname, artist, cover, voters, radioid, nameseo }, ref) => {
    const { isDisplay } = useDisplayRadio();
    const { stackModal: stackModalVote } = useModal(VOTE_MODAL);
    const { stackModal: stackModalSelect } = useModal(ADD_SONG_TO_RADIO_MODAL);
    const { data: nextSongsData } = useNextSong(radioid);

    const [voted, votes, position, scid] = useMemo(() => {
      if (nextSongsData) {
        const index = nextSongsData.findIndex((song) => song.songid === songid);
        if (index > -1) {
          return [
            true,
            nextSongsData[index].votes,
            index,
            nextSongsData[index].scid,
          ];
        }
      }
      return [false, undefined, undefined];
    }, [songid, nextSongsData]);

    const handleOnVote = () => {
      stackModalVote({
        songid: songid,
        songname: songname,
        artist: artist,
        cover: cover,
        scid: scid,
        radioid: radioid,
      });
    };

    const handleSelectRadio = (radioid) => {
      stackModalSelect({
        songid: songid,
        songname: songname,
        artist: artist,
        cover: cover,
        scid: scid,
        radioid: radioid,
      });
    };

    const coverHttps = cover
      ? cover.replace("http://data", "https://data")
      : "";

    const cleanSongname = useMemo(
      () => cleanSongTitle(songname, artist),
      [songname, artist]
    );

    const handleClick = () => {
      handleOnVote();
    }

    return (
      <div
        ref={ref}
        className={["song", voted ? "voted" : ""].join(" ")}
        title={songid}
        onClick={handleClick}
      >
        <div className="song-main-row">
          {voters ? (
            <div className="voter-container">
              {voters.map((voter) => (
                <Voter key={voter.userid} {...voter} />
              ))}
            </div>
          ) : null}
          {coverHttps !== "" ? (
            <LoadableImage src={coverHttps} alt={songname} className="cover" />
          ) : null}
          <div className="song-info">
            <div className="songname">{cleanSongname}</div>
            <div className="artist">{artist}</div>
          </div>
          {!isDisplay ? <FavoriteButton songid={songid} /> : null}
          {(radioid !== null && radioid !== undefined) ||
            (isDisplay && voted) ? (
            <Button
              className={["vote", voted ? "brand" : "play blue"].join(" ")}
              size="small"
              onClick={handleOnVote}
              as={voted ? "text" : "button"}
            >
              {voted ? (
                <>
                  <small>#{position + 1}</small> <span>{votes}</span>
                </>
              ) : (
                <PlayIconComponent nameseo={nameseo} />
              )}
            </Button>
          ) : !isDisplay ? (
            <Button
              className={["vote", "play", "blue", "select-radio"].join(" ")}
              size="small"
              onClick={handleSelectRadio}
            >
              <PlayIconComponent nameseo={nameseo} />
            </Button>
          ) : null}
        </div>
        {/* <DevPre>{songid}</DevPre> */}
      </div>
    );
  }
);

function PlayIconComponent({ nameseo }) {
  if (nameseo && nameseo.includes("bemol-music")) {
    return <PlayIconFlat />;
  }
  return <PlayIcon />;
}

export default Song;

/**
 * Renders the voter user avatar with its name in the title
 * if the voter doesn't have an avatar it displays the name initials
 * @param {object} props - voter information
 * @param {string} props.name - voter name
 * @param {string} props.avatar - voter avatar url
 * @returns
 */
const Voter = ({ name, avatar }) => {
  const { textColor, main: mainColor } = useMemo(() => {
    return colorFromString(name);
  }, [name]);

  const initials = useMemo(() => {
    if (typeof name !== "string" || name === "") return "";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("");
  }, [name]);

  return (
    <div
      className="voter"
      style={{
        backgroundColor: mainColor,
        color: textColor,
        backgroundImage: `url(${avatar})`,
      }}
      title={name}
    >
      {avatar === "" ? initials : ""}
    </div>
  );
};
